import {
  AssistantButton,
  type AssistantButtonProps,
} from "#src/components/Assistant/AssistantButton";
import { AssistantPopover } from "#src/components/Assistant/AssistantPopover";
import { useAssistant } from "#src/contexts/Assistant";
import { Button } from "@validereinc/common-components";
import React, { useEffect } from "react";
import { useLocation } from "react-router";

export const AssistantGlobal = ({
  isLoading: isLoadingProp,
  disabled: isDisabledProp,
}: Pick<AssistantButtonProps, "isLoading" | "disabled">) => {
  const { pathname } = useLocation();
  const {
    isOpen,
    setIsOpen,
    isSeen,
    setIsSeen,
    content,
    contentStatic,
    contentFooter,
    isContentRevealed,
    setIsContentRevealed,
    error,
    isLoading,
    loadingStateLabel,
    isBusy,
    isDisabled,
    clearContent,
    contentTiedToLocation,
    isContentTiedToCurrentLocation,
    contentStaticTiedToLocation,
    isContentStaticTiedToCurrentLocation,
    hasHydrated,
  } = useAssistant();

  useEffect(
    () =>
      useAssistant.subscribe((state, prevState) => {
        if (
          state.isContentTiedToCurrentLocation &&
          state.contentTiedToLocation !== pathname &&
          state.content &&
          prevState.isLoading &&
          state.isLoading
        ) {
          state.setContentTiedToLocation(pathname);
        }

        if (
          state.isContentStaticTiedToCurrentLocation &&
          !prevState.contentStaticTiedToLocation &&
          prevState.contentStaticTiedToLocation !== pathname &&
          state.contentStatic
        ) {
          state.setContentStaticTiedToLocation(pathname);
        }
      }),
    [pathname]
  );

  const showContent = Boolean(
    (isContentTiedToCurrentLocation &&
      contentTiedToLocation &&
      contentTiedToLocation === pathname) ||
      (!isContentTiedToCurrentLocation && contentStatic === null)
  );
  const showContentStatic = Boolean(
    (isContentStaticTiedToCurrentLocation &&
      contentStaticTiedToLocation &&
      contentStaticTiedToLocation === pathname) ||
      !isContentStaticTiedToCurrentLocation
  );
  const showButton =
    (isContentTiedToCurrentLocation &&
      contentTiedToLocation &&
      contentTiedToLocation === pathname) ||
    (isContentStaticTiedToCurrentLocation &&
      contentStaticTiedToLocation &&
      contentStaticTiedToLocation === pathname);

  if (!hasHydrated) return null;

  return (
    <AssistantPopover
      shouldDisplayArrow={false}
      rootProps={{
        open: isOpen,
        onOpenChange: (newOpenState) => {
          setIsOpen(newOpenState);

          // when the assistant gets closed while the content is set, we want to
          // mark it as seen, so the assistant isn't calling for attention or
          // retyping with animations next time it's opened
          if (!newOpenState && (content || contentStatic)) setIsSeen(true);
        },
      }}
      contentProps={{
        align: "end",
      }}
      content={showContent ? content : showContentStatic ? contentStatic : null}
      contentFooter={showContent ? contentFooter : null}
      isStaticContentShown={!showContent}
      isContentRevealed={isContentRevealed}
      setIsContentRevealed={setIsContentRevealed}
      error={error}
      errorSuggestion="Refresh the Assistant to try again."
      errorAction={
        <Button
          onClick={() => {
            clearContent();
            setIsOpen(false);
          }}
          variant="primary"
          size="small"
        >
          Refresh
        </Button>
      }
      onClose={() => setIsOpen(false)}
      onGoBack={() => clearContent()}
      isLoading={isLoading}
      loadingStateLabel={loadingStateLabel}
      isBusy={isBusy}
      isSeen={isSeen}
    >
      {showButton && (
        <AssistantButton
          isReady={showContent && content !== null && content !== "" && !isOpen}
          isSeen={isSeen}
          isActive={isOpen}
          isLoading={isLoadingProp}
          disabled={isDisabled || isDisabledProp}
          onClick={() => setIsOpen(true)}
        />
      )}
    </AssistantPopover>
  );
};
